import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Modal from '@components/common/Modal/Modal';
import styles from './Join.module.css';
import { useAuthStore } from '@store/authStore';
import { SnsLoginCheckRes } from '@/api/login/type';
import { api } from '@/api/login/api';
import { TERMS_CONTENT } from './TermsContent'; // ← 분리된 약관 파일 import

const JOIN_TYPE_LABEL: Record<number, string> = {
    0: '네이버',
    1: '카카오',
    2: '구글',
};

const JOIN_TYPE_STYLE: Record<number, { background: string; color: string }> = {
    0: { background: '#e8f9f0', color: '#03C75A' },
    1: { background: '#fffbe6', color: '#b8860b' },
    2: { background: '#edf1fe', color: '#4285F4' },
};

const SNS_ICON: Record<number, string> = {
    0: 'ri-naver-fill',
    1: 'ri-kakao-talk-fill',
    2: 'ri-google-fill',
};

const Join = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { setTokenFromLogin } = useAuthStore();

    const snsInfo: SnsLoginCheckRes = location.state?.snsInfo;

    const [form, setForm] = useState({
        userName: snsInfo?.snsName ?? '',
        phone: snsInfo?.snsPhone ?? '',
        email: snsInfo?.snsEmail ?? '',
    });

    const [agrees, setAgrees] = useState({
        service: false,
        privacy: false,
        corporate: false,
        marketing: false,
    });

    const [modalKey, setModalKey] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(false);

    if (!snsInfo) {
        navigate('/login');
        return null;
    }

    const handleInputChange = (field: string, value: string) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const toggleAgree = (key: keyof typeof agrees) => {
        setAgrees(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const toggleAll = () => {
        const allChecked = Object.values(agrees).every(Boolean);
        const next = !allChecked;
        setAgrees({
            service: next,
            privacy: next,
            corporate: next,
            marketing: next,
        });
    };

    const validate = () => {
        const errs: Record<string, string> = {};
        if (!form.userName.trim()) errs.userName = '사용자명을 입력해주세요.';
        if (!form.phone.trim()) errs.phone = '전화번호를 입력해주세요.';
        if (!form.email.trim()) errs.email = '이메일을 입력해주세요.';
        if (!agrees.privacy) errs.agrees = '개인정보 수집 동의가 필요합니다.';
        if (!agrees.service) errs.agrees = '이용약관에 동의해주세요.';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate() || isLoading) return;
        setIsLoading(true);
        try {
            const result = await api.snsJoin({
                joinType: snsInfo.joinType!,
                userId: snsInfo.snsId!,
                userName: form.userName,
                phone: form.phone,
                email: form.email,
                agreeTerms: agrees.service,
                agreePrivacy: agrees.privacy,
                agreeMarketing: agrees.marketing,
                snsToken: snsInfo.snsToken!,
                redirectUri: snsInfo.redirectUri,
            });
            if (result.data?.accessToken) {
                setTokenFromLogin(result.data);
                navigate('/user/dashboard');
            }
        } catch (e) {
            console.error('가입 실패', e);
        } finally {
            setIsLoading(false);
        }
    };

    const termsItems = [
        { key: 'service', label: '이용약관', required: true },
        {
            key: 'privacy',
            label: '개인정보 수집 이용 및 제공 동의',
            required: true,
        },
        { key: 'marketing', label: '마케팅 정보 수신 동의', required: false },
    ] as const;

    const allChecked = Object.values(agrees).every(Boolean);
    const joinType = snsInfo.joinType ?? 0;
    const badgeStyle = JOIN_TYPE_STYLE[joinType];

    return (
        <div className={styles.pageWrap}>
            {/* 약관 모달 */}
            {modalKey &&
                (() => {
                    const { title, Component } = TERMS_CONTENT[modalKey];
                    return (
                        <Modal
                            title={title}
                            size="lg"
                            onClose={() => setModalKey(null)}
                        >
                            <div
                                style={{
                                    maxHeight: 400,
                                    overflowY: 'auto',
                                    fontSize: 13,
                                    lineHeight: 1.8,
                                }}
                                className="text-muted"
                            >
                                <Component />
                            </div>
                        </Modal>
                    );
                })()}

            <div className={styles.card}>
                {/* 헤더 */}
                <div className={styles.header}>
                    <img
                        src="/images/common/logo/logo_dark.png"
                        alt="logo"
                        className={styles.logo}
                    />
                </div>

                {/* SNS 뱃지 */}
                <div className={styles.snsBadgeWrap}>
                    <div>
                        <span
                            className={styles.snsBadge}
                            style={{
                                background: badgeStyle.background,
                                color: badgeStyle.color,
                            }}
                        >
                            <i
                                className={SNS_ICON[joinType]}
                                style={{ fontSize: 15 }}
                            />
                            {JOIN_TYPE_LABEL[joinType]} 계정으로 회원가입
                        </span>
                    </div>
                    <p className={styles.snsBadgeDesc}>
                        추가 정보를 입력하고 서비스를 시작하세요.
                    </p>
                </div>

                {/* 입력 폼 */}
                <p className={styles.sectionTitle}>기본 정보</p>

                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                        사용자명 <span className={styles.required}>*</span>
                    </label>
                    <input
                        type="text"
                        className={`${styles.formInput} ${errors.userName ? styles.inputError : ''}`}
                        placeholder="이름을 입력해주세요."
                        value={form.userName}
                        onChange={e =>
                            handleInputChange('userName', e.target.value)
                        }
                    />
                    {errors.userName && (
                        <p className={styles.errorMsg}>
                            <i className="ri-error-warning-line" />
                            {errors.userName}
                        </p>
                    )}
                </div>

                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                        전화번호 <span className={styles.required}>*</span>
                    </label>
                    <input
                        type="tel"
                        className={`${styles.formInput} ${errors.phone ? styles.inputError : ''}`}
                        placeholder="전화번호를 입력해주세요."
                        value={form.phone}
                        onChange={e =>
                            handleInputChange('phone', e.target.value)
                        }
                    />
                    {errors.phone && (
                        <p className={styles.errorMsg}>
                            <i className="ri-error-warning-line" />
                            {errors.phone}
                        </p>
                    )}
                </div>

                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                        이메일 <span className={styles.required}>*</span>
                    </label>
                    <input
                        type="email"
                        className={`${styles.formInput} ${errors.email ? styles.inputError : ''}`}
                        placeholder="이메일을 입력해주세요."
                        value={form.email}
                        onChange={e =>
                            handleInputChange('email', e.target.value)
                        }
                    />
                    {errors.email && (
                        <p className={styles.errorMsg}>
                            <i className="ri-error-warning-line" />
                            {errors.email}
                        </p>
                    )}
                </div>

                <div className={styles.divider} />

                {/* 약관 */}
                <p className={styles.sectionTitle}>약관 동의</p>

                <div className={styles.termsBox}>
                    {/* 전체 동의 */}
                    <div className={styles.termsAllRow} onClick={toggleAll}>
                        <input
                            type="checkbox"
                            className={styles.checkbox}
                            id="agreeAll"
                            checked={allChecked}
                            onChange={toggleAll}
                            onClick={e => e.stopPropagation()}
                        />
                        <label
                            htmlFor="agreeAll"
                            className={styles.termsAllLabel}
                        >
                            전체 동의
                        </label>
                    </div>

                    {/* 개별 약관 */}
                    {termsItems.map(item => (
                        <div key={item.key} className={styles.termsRow}>
                            <input
                                type="checkbox"
                                className={`${styles.checkbox} ${styles.checkboxSm}`}
                                id={`agree_${item.key}`}
                                checked={agrees[item.key]}
                                onChange={() => toggleAgree(item.key)}
                            />
                            <label
                                htmlFor={`agree_${item.key}`}
                                className={styles.termsLabel}
                            >
                                {item.label}
                                <span
                                    className={`${styles.termsBadge} ${
                                        item.required
                                            ? styles.termsBadgeRequired
                                            : styles.termsBadgeOptional
                                    }`}
                                >
                                    {item.required ? '필수' : '선택'}
                                </span>
                            </label>
                            <button
                                type="button"
                                className={styles.termsViewBtn}
                                onClick={() => setModalKey(item.key)}
                            >
                                보기
                            </button>
                        </div>
                    ))}

                    {errors.agrees && (
                        <div className={styles.termsError}>
                            <i className="ri-error-warning-line" />
                            {errors.agrees}
                        </div>
                    )}
                </div>

                {/* 버튼 */}
                <div className={styles.btnRow}>
                    <button
                        type="button"
                        className={styles.btnCancel}
                        onClick={() => navigate('/login')}
                        disabled={isLoading}
                    >
                        취소
                    </button>
                    <button
                        type="button"
                        className={styles.btnSubmit}
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <span className={styles.spinner} />
                                처리 중...
                            </>
                        ) : (
                            <>
                                <i className="ri-user-add-line" />
                                가입하기
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Join;
